import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../Modal';
import { spacing } from '../../../theme-v2';
// colors, typography unused while the subtitle below is commented out
import {
  ShareAccessFields,
  isValidSharePassword,
  type ShareAccess,
  type ShareRestriction,
} from './ShareAccessFields';
import { isValidShareDomain, isValidShareEmail } from './shareValidation';

/** Extract valid email addresses from a CSV file (comma / newline separated). */
export const parseCsvEmails = async (file: File): Promise<string[]> => {
  const content = await file.text();
  const emailRegex = /^[^\s@]+@(?:[^\s@.]+\.)+[^\s@.]+$/;
  return content
    // ";" for CSVs exported by Excel in FR/ES locales.
    .split(/[\n,;]/)
    .map((part) => part.trim().replace(/"/g, ''))
    .filter((part) => emailRegex.test(part));
};

export type ShareSettings =
  | { access: 'public' }
  | { access: 'password'; password: string }
  | { access: 'email'; emails: string[] };

export interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  shareUrl: string;
  onSave: (settings: ShareSettings) => void | Promise<void>;
  saving?: boolean;
  initialAccess?: ShareAccess;
  className?: string;
}

export const ShareModal = ({
  open,
  onClose,
  shareUrl,
  onSave,
  saving,
  initialAccess = 'public',
  className,
}: ShareModalProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const [access, setAccess] = useState<ShareAccess>(initialAccess);
  // The saved password can't be read back, so this always starts empty.
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [restriction, setRestriction] = useState<ShareRestriction>('people');
  const [emailDraft, setEmailDraft] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [domain, setDomain] = useState('');
  const [csvNote, setCsvNote] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [domainInvalid, setDomainInvalid] = useState(false);

  const changeEmailDraft = (value: string) => {
    setEmailDraft(value);
    setEmailInvalid(false);
  };

  const changeDomain = (value: string) => {
    setDomain(value);
    setDomainInvalid(false);
  };

  // Leaving a mode discards what was entered in it, so only the visible mode is saved.
  const changeRestriction = (next: ShareRestriction) => {
    if (next === restriction) return;
    setEmails([]);
    setEmailDraft('');
    setEmailInvalid(false);
    setCsvNote('');
    setDomain('');
    setDomainInvalid(false);
    setRestriction(next);
  };

  const addEmail = () => {
    const value = emailDraft.trim();
    if (!value) return;
    if (!isValidShareEmail(value)) {
      setEmailInvalid(true);
      return;
    }
    if (emails.includes(value)) return;
    setEmails((prev) => [...prev, value]);
    setEmailDraft('');
  };

  const removeEmail = (email: string) =>
    setEmails((prev) => prev.filter((x) => x !== email));

  const handleCsvFile = async (file: File) => {
    try {
      const parsed = await parseCsvEmails(file);
      if (parsed.length === 0) {
        setCsvNote(t('experienceV2.share.people.csvNoEmails'));
        return;
      }
      const next = Array.from(new Set([...emails, ...parsed]));
      setEmails(next);
      setCsvNote(
        t('experienceV2.share.people.csvImported', {
          count: next.length - emails.length,
        }),
      );
    } catch {
      setCsvNote(t('experienceV2.share.people.csvNoEmails'));
    }
  };

  const handleSave = () => {
    if (access === 'public') {
      return onSave({ access: 'public' });
    }
    if (access === 'password') {
      if (!isValidSharePassword(password, confirmPassword)) return;
      return onSave({ access: 'password', password });
    }
    if (restriction === 'domain') {
      const trimmedDomain = domain.trim();
      if (trimmedDomain && !isValidShareDomain(trimmedDomain)) {
        setDomainInvalid(true);
        return;
      }
      return onSave({
        access: 'email',
        emails: trimmedDomain ? [`*@${trimmedDomain}`] : [],
      });
    }
    const draft = emailDraft.trim();
    if (draft && !isValidShareEmail(draft)) {
      setEmailInvalid(true);
      return;
    }
    const allowed =
      draft && !emails.includes(draft) ? [...emails, draft] : emails;
    return onSave({ access: 'email', emails: allowed });
  };

  // Public → Restricted with no recipient would lock everyone out (MED-7325).
  const hasRecipient =
    restriction === 'domain'
      ? domain.trim() !== ''
      : emails.length > 0 || emailDraft.trim() !== '';
  const needsRecipient =
    initialAccess !== 'email' && access === 'email' && !hasRecipient;
  // A typed-but-not-added email that is invalid blocks Save (no error shown).
  const draftInvalid =
    access === 'email' &&
    restriction === 'people' &&
    emailDraft.trim() !== '' &&
    !isValidShareEmail(emailDraft.trim());

  return (
    <Modal
      className={className}
      open={open}
      onClose={onClose}
      onConfirm={handleSave}
      confirmLoading={saving}
      confirmDisabled={
        needsRecipient ||
        draftInvalid ||
        (access === 'password' &&
          !isValidSharePassword(password, confirmPassword))
      }
      title={t('experienceV2.share.title')}
      confirmText={t('experienceV2.share.save')}
      cancelText={t('experienceV2.share.cancel')}
      size="medium"
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: spacing.base }}
      >
        {/* Subtitle commented out per product request.
        <p
          style={{
            margin: `0 0 ${spacing.xs}px`,
            fontSize: typography.size.md,
            color: colors.text.secondary,
          }}
        >
          {t('experienceV2.share.subtitle')}
        </p>
        */}

        <ShareAccessFields
          access={access}
          onAccessChange={setAccess}
          shareUrl={shareUrl}
          restriction={restriction}
          onRestrictionChange={changeRestriction}
          emailDraft={emailDraft}
          onEmailDraftChange={changeEmailDraft}
          onAddEmail={addEmail}
          emails={emails}
          onRemoveEmail={removeEmail}
          domain={domain}
          onDomainChange={changeDomain}
          onCsvFile={(file) => void handleCsvFile(file)}
          csvNote={csvNote}
          password={password}
          onPasswordChange={setPassword}
          confirmPassword={confirmPassword}
          onConfirmPasswordChange={setConfirmPassword}
          emailError={
            emailInvalid
              ? t('experienceV2.share.validation.invalidEmail')
              : undefined
          }
          domainError={
            domainInvalid
              ? t('experienceV2.share.validation.invalidDomain')
              : undefined
          }
          validationError={
            needsRecipient
              ? t('experienceV2.share.validation.recipientRequired')
              : undefined
          }
        />
      </div>
    </Modal>
  );
};
